import { useEffect, ReactElement, FC } from 'react';
import {
  HStack,
  VStack,
  Heading,
  Button,
  useBreakpointValue,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  ButtonProps,
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';

export interface PageHeadingAction {
  label: string;
  icon?: ReactElement;
  colorScheme?: string;
  variant?: ButtonProps['variant'];
  size?: 'xs' | 'sm' | 'md' | 'lg';
  isDisabled?: boolean;
  isLoading?: boolean;
  onClick: () => void;
}

export interface PageHeadingProps {
  title: string;
  actions?: PageHeadingAction[];
  subtitle?: string;
}

export const PageHeading: FC<PageHeadingProps> = ({
  title,
  actions = [],
  subtitle,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { t } = useTranslation();
  useEffect(() => {
    const titleElement = document.querySelector('title');
    if (titleElement) {
      titleElement.innerHTML = `${title} - BeMap`;
    }
  }, [title]);

  const renderDesktopActions = () => (
    <HStack spacing={3}>
      {actions.map((action, index) => (
        <Button
          key={index}
          colorScheme={action.colorScheme || 'blue'}
          variant={action.variant || 'solid'}
          size={action.size || 'md'}
          leftIcon={action.icon}
          isDisabled={action.isDisabled}
          isLoading={action.isLoading}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      ))}
    </HStack>
  );

  const renderMobileActions = () => {
    if (actions.length === 0) return null;

    if (actions.length === 1) {
      const action = actions[0];
      return (
        <Button
          colorScheme={action.colorScheme || 'blue'}
          variant={action.variant || 'solid'}
          size={action.size || 'md'}
          leftIcon={action.icon}
          isDisabled={action.isDisabled}
          isLoading={action.isLoading}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      );
    }

    // Show first action as button, rest in dropdown
    const firstAction = actions[0];
    const remainingActions = actions.slice(1);

    return (
      <HStack spacing={3}>
        <Button
          colorScheme={firstAction.colorScheme || 'blue'}
          variant={firstAction.variant || 'solid'}
          size={firstAction.size || 'sm'}
          leftIcon={firstAction.icon}
          isDisabled={firstAction.isDisabled}
          isLoading={firstAction.isLoading}
          onClick={firstAction.onClick}
        >
          {firstAction.label}
        </Button>

        <Menu placement="bottom-end">
          <MenuButton
            as={IconButton}
            aria-label={t('table.actions')}
            icon={<HamburgerIcon />}
            colorScheme="gray"
            variant="outline"
            size={firstAction.size || 'sm'}
          />
          <Portal>
            <MenuList>
              {remainingActions.map((action, index) => (
                <MenuItem
                  key={index}
                  icon={action.icon}
                  onClick={action.onClick}
                  isDisabled={action.isDisabled}
                >
                  {action.label}
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </HStack>
    );
  };

  return (
    <>
      <HStack justify="space-between" pt={4} w="100%" flexWrap={'wrap'}>
        <VStack align="start">
          <Heading size="lg">{title}</Heading>
          {subtitle && (
            <Heading size="sm" color="gray.500" fontWeight="normal">
              {subtitle}
            </Heading>
          )}
        </VStack>
        {isMobile ? renderMobileActions() : renderDesktopActions()}
      </HStack>
    </>
  );
};
